/**
 * What each animal can actually DO.
 *
 * Roles made the world treat you differently. Verbs make you *play* differently — the
 * other half of Kate's "have fun being different animals". Without these, being a bunny
 * is being a wolf with worse numbers.
 *
 * Each verb below exists because it changes the player's decisions, not because it is a
 * feature:
 *   Freeze   — prey cannot win a fight, so stillness is their only real defence.
 *   Trample  — a bear ignores what stops everything else, so terrain means less to it.
 *   Fly      — a bird leaves the ground and climbs to the treetops.
 *   Pounce   — a cat closes the last gap in one movement rather than a chase.
 */
import type { SpeciesId } from './species'
import { IDENTITY } from './identity'

export type Verb = 'freeze' | 'trample' | 'fly' | 'pounce' | 'sniff' | 'graze'

export interface VerbInfo {
  id: Verb
  label: string
  /** What the button says, and what the player is told they can do. */
  hint: string
}

export const VERBS: Record<Verb, VerbInfo> = {
  freeze: { id: 'freeze', label: 'FREEZE',
    hint: 'Hold still. Predators lose track of you while you do not move.' },
  trample: { id: 'trample', label: 'TRAMPLE',
    hint: 'Smash straight through undergrowth. Everything nearby scatters.' },
  /*
   * Kate, 2026-08-23: "if you're a bird, when you press a button fly, you actually
   * have to fly. Like whenever I push a button fly, if I'm a bird, it doesn't let me
   * fly." It was SOAR, it only did anything once you were already airborne, and
   * nothing on screen said "fly". Hold this and you go up.
   */
  fly: { id: 'fly', label: 'FLY',
    hint: 'Hold it to climb, as high as the trees.' },
  pounce: { id: 'pounce', label: 'POUNCE',
    hint: 'Leap the last few metres onto something.' },
  sniff: { id: 'sniff', label: 'SNIFF',
    hint: 'Crouch to smell where prey went.' },
  graze: { id: 'graze', label: 'GRAZE',
    hint: 'Eat the grass where you stand.' },
}

/** The verb this animal gets. One each, so it defines them rather than clutters them. */
export function verbFor(species: SpeciesId): Verb | null {
  switch (species) {
    case 'bunny': case 'chameleon': case 'fish': return 'freeze'
    case 'bear': case 'whale': return 'trample'
    case 'bird': return 'fly'
    case 'cat': return 'pounce'
    case 'wolf': case 'dog': return 'sniff'
    case 'deer': case 'camel': return 'graze'
    default: return null
  }
}

/** Prey animals cannot fight at all. Kate's bunny's "weapon" is its speed, not its teeth. */
export function canFight(species: SpeciesId): boolean {
  const role = IDENTITY[species].role
  return role !== 'prey' && role !== 'critter'
}

/** How long a freeze takes to fully hide you, and how completely. */
export const FREEZE_TIME = 1.2
export const FREEZE_HIDDEN = 0.95

/** Trample clears undergrowth within this radius and scatters animals within twice it. */
export const TRAMPLE_RADIUS = 3.2
/** Pounce distance and how long the leap takes. */
export const POUNCE_RANGE = 7
export const POUNCE_SPEED = 15
